"use client";

import { SegmentControl } from "@/components/SegmentControl";
import { Translate } from "@/components/Translate";
import { ChevronLeft, ChevronRight } from "@/components/icons/Chevron";
import { StickyTracker } from "@/components/ui/Sticky";
import { Link } from "@/i18n/Link";
import { sbLink } from "@/lib/storyblok/link";
import type {
	BlokCategorySection,
	BlokLink,
	BlokNavigationSection,
	BlokSubMenu,
} from "@/storyblok";
import { clsx } from "@frend-digital/ui";
import { memo } from "react";
import type { StoryblokMultilink } from "storyblok";
import { useStateParams } from "../../../hooks/use-state-params";
import styles from "./Header.module.css";
import { OutletTag } from "./OutletTag";

// Mirrors the URL-based Outlet detection in MobileMenuContent.tsx — the nav schema
// has no tag/badge field, so "Outlet" is identified by its link rather than a CMS flag.
const isOutletLink = (href?: string) => Boolean(href?.includes("outlet"));

export const MobileSection = memo(
	({
		section,
		index = 0,
		preview,
		removeHrefs,
		expanded,
	}: {
		section: BlokCategorySection | BlokNavigationSection | BlokLink | BlokSubMenu;
		index?: number;
		preview: boolean;
		/**
		 * Hrefs to be removed as they exist other places in the menu
		 */
		removeHrefs?: string[];
		expanded?: boolean | undefined;
	}) => {
		const key = `${section.title}${index === 0 ? "" : `-${index}`}`;

		const expandedOnMobile =
			section.component === "subMenu" ? (section.expandOnMobile ?? false) : false;

		const searchParams = useStateParams();

		const isActive = searchParams.has("section", key);

		if (section.component === "navigationSection") {
			const isOutlet = isOutletLink(section.link?.cached_url);

			return (
				<>
					<button
						data-slide-left
						type="button"
						onClick={() => {
							if (isActive) {
								return searchParams.delete("section", key);
							}
							searchParams.append("section", key);
						}}
						className={clsx(styles.largeTitle)}
					>
						<span className={styles.linkRowTop}>
							{section.title}
							{isOutlet && <OutletTag />}
						</span>

						<ChevronRight />
					</button>
					<article
						inert={!isActive}
						data-active={isActive ? isActive : undefined}
						className={clsx(styles.mobileSection, isActive && styles.active)}
					>
						<div className={styles.panelScroll}>
							<StickyTracker asChild>
								<button
									className={styles.back}
									type="button"
									onClick={() => {
										searchParams.delete("section", key);
									}}
								>
									<ChevronLeft />
									<Translate>Back</Translate>
								</button>
							</StickyTracker>
							<nav className={styles.navGroup}>
								{section.sections?.map((section) => {
									return (
										<MobileSection
											preview={preview}
											key={section._uid}
											section={section}
											index={index + 1}
										/>
									);
								})}
							</nav>
						</div>
					</article>
				</>
			);
		}

		if (section.component === "subMenu") {
			if (expandedOnMobile && index === 1) {
				return (
					<>
						<nav className={styles.navGroup}>
							{section.items?.map((section) => {
								return (
									<MobileSection
										preview={preview}
										key={section._uid}
										section={section}
										index={index + 1}
										expanded={false}
									/>
								);
							})}
						</nav>
					</>
				);
			}
			return (
				<>
					<button
						type="button"
						onClick={() => {
							if (isActive) {
								return searchParams.delete("section", key);
							}
							searchParams.append("section", key);
						}}
						className={clsx(styles.subTitle)}
					>
						{section.title}

						<ChevronRight />
					</button>
					<article
						inert={!isActive}
						data-active={isActive ? isActive : undefined}
						className={clsx(styles.mobileSection, isActive && styles.active)}
					>
						<div className={styles.panelScroll}>
							<StickyTracker asChild>
								<button
									className={styles.back}
									type="button"
									onClick={() => {
										searchParams.delete("section", key);
									}}
								>
									<ChevronLeft />
									<Translate>Back</Translate>
								</button>
							</StickyTracker>
							<nav className={styles.navGroup}>
								{section.items?.map((section) => {
									return (
										<MobileSection
											preview={preview}
											key={section._uid}
											section={section}
											index={index + 1}
											expanded={expandedOnMobile}
										/>
									);
								})}
							</nav>
						</div>
					</article>
				</>
			);
		}

		if (section.component === "link") {
			const link = sbLink(section.link as StoryblokMultilink);
			if (!link) return null;

			if (index === 0) {
				const isOutlet = isOutletLink(link.href);
				// Outlet always shows here (with its tag) even though the same href also
				// exists in the bottomNav quick-links list — the general dedup below is
				// meant for ordinary duplicate links, not this one.
				if (!isOutlet && removeHrefs?.includes(link.href)) return null;
				return (
					<SegmentControl segments={section}>
						<Link data-slide-left href={link.href} className={styles.largeTitle}>
							<span className={styles.linkRowTop}>
								{section.text}
								{isOutlet && <OutletTag />}
							</span>

							<ChevronRight />
						</Link>
					</SegmentControl>
				);
			}

			return (
				<SegmentControl segments={section}>
					<Link className={styles.subTitle} href={link.href}>
						{section.text} <ChevronRight />
					</Link>
				</SegmentControl>
			);
		}
	},
);
